import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import 'react-datepicker/dist/react-datepicker.css'
import { connect } from 'react-redux'
import { UserAvatar } from '../../../../assets/icons'
import { capitalizeEachWord } from '../../../../utils/stringsUtils'
import { formatDayMonth, formatHourMinute, toDateOnly } from '../../../../utils/formatDate'
import { getNearestTurno } from '../../../../utils/groupTurnosByProfessional'
import useIsMobile from '../../../../hooks/useIsMobile'
import { Button } from '../../../../components/ui/button'
import { BackPageHeader } from '../../../../components/ui/header'
import CancelAppointmentConfirmModal from '../../../../components/CancelAppointmentConfirmModal'
import { BOOKING_TYPES } from '../constants'
import RecommendationCard from './RecommendationCard'

const getTurnSpecialtyKey = turn =>
	turn?.idEspecialidad ?? turn?.especialidad ?? turn?.speciality ?? null

const getSpecialtiesToEvaluate = submittedFilters => {
	if (Array.isArray(submittedFilters?.specialities) && submittedFilters.specialities.length > 0) {
		return submittedFilters.specialities
	}

	return submittedFilters?.speciality ? [submittedFilters.speciality] : []
}

const getFirstCommonDate = (allTurnos, submittedFilters) => {
	if (!Array.isArray(allTurnos) || allTurnos.length === 0) return null
	const searchedSpecialties = getSpecialtiesToEvaluate(submittedFilters).map(String)
	const datesBySpecialty = new Map()

	allTurnos.forEach(turno => {
		const specialtyKey = getTurnSpecialtyKey(turno)

		if (specialtyKey == null || !turno?.fechaTurnoDateTime) return

		const normalizedSpecialtyKey = String(specialtyKey)
		const dayKey = toDateOnly(turno.fechaTurnoDateTime).getTime()

		if (!datesBySpecialty.has(normalizedSpecialtyKey)) {
			datesBySpecialty.set(normalizedSpecialtyKey, new Set())
		}

		datesBySpecialty.get(normalizedSpecialtyKey).add(dayKey)
	})

	const specialtyKeys =
		searchedSpecialties.length > 0
			? searchedSpecialties
			: Array.from(datesBySpecialty.keys())

	if (specialtyKeys.length === 0) return null

	// Si alguna especialidad buscada no tiene turnos, no puede haber fecha en común
	if (specialtyKeys.some(key => !datesBySpecialty.has(String(key)))) {
		return null
	}

	// Elegimos el conjunto con menos fechas para minimizar las iteraciones
	const sets = specialtyKeys.map(key => datesBySpecialty.get(String(key)))
	const baseSet = sets.reduce((smallest, current) =>
		current.size < smallest.size ? current : smallest
	)

	let firstCommonDayKey = null

	for (const dayKey of baseSet) {
		if (
			specialtyKeys.every(specialtyKey =>
				datesBySpecialty.get(String(specialtyKey)).has(dayKey)
			)
		) {
			if (firstCommonDayKey === null || dayKey < firstCommonDayKey) {
				firstCommonDayKey = dayKey
			}
		}
	}

	return firstCommonDayKey === null ? null : new Date(firstCommonDayKey)
}
registerLocale('es', es)

const SelectAppointment = ({
	turnosDisponibles = [],
	allTurnos = [],
	submittedFilters,
	bookingMode,
	selectedTurn,
	onBack,
	onConfirm,
	clinics
}) => {
	const isMobile = useIsMobile()
	const isMultiple = bookingMode === BOOKING_TYPES.MULTIPLE
	const turnos = turnosDisponibles
	const nearest = useMemo(() => getNearestTurno(turnos), [turnos])
	const selectedBaseTurn = useMemo(() => {
		if (selectedTurn && turnos.some(turno => turno.idTurno === selectedTurn.idTurno)) {
			return selectedTurn
		}

		return nearest
	}, [selectedTurn, turnos, nearest])
	const [selectedDate, setSelectedDate] = useState(
		selectedBaseTurn ? new Date(selectedBaseTurn.fechaTurnoDateTime) : null
	)
	const [selectedTurnoId, setSelectedTurnoId] = useState(selectedBaseTurn?.idTurno || null)
	const [showContinueAsSingleModal, setShowContinueAsSingleModal] = useState(false)

	useEffect(() => {
		if (selectedBaseTurn) {
			setSelectedDate(new Date(selectedBaseTurn.fechaTurnoDateTime))
			setSelectedTurnoId(selectedBaseTurn.idTurno)
			return
		}

		setSelectedDate(null)
		setSelectedTurnoId(null)
	}, [selectedBaseTurn])

	const availableDates = useMemo(() => {
		const uniq = new Map()
		turnos.forEach(t => {
			const day = toDateOnly(t.fechaTurnoDateTime).getTime()
			uniq.set(day, new Date(day))
		})
		return Array.from(uniq.values()).sort((a, b) => a - b)
	}, [turnos])

	const turnosByDate = useMemo(() => {
		if (!selectedDate) return []
		const dayKey = toDateOnly(selectedDate).getTime()
		return turnos
			.filter(t => toDateOnly(t.fechaTurnoDateTime).getTime() === dayKey)
			.sort((a, b) => new Date(a.fechaTurnoDateTime) - new Date(b.fechaTurnoDateTime))
	}, [selectedDate, turnos])

	const handleSelectDate = date => {
		setSelectedDate(date)
		setSelectedTurnoId(null)
	}

	const handleSelectHour = turnoId => {
		setSelectedTurnoId(turnoId)
	}

	const getSelectedTurn = () => {
		if (!selectedTurnoId) return null
		return turnos.find(t => t.idTurno === selectedTurnoId)
	}

	const handleContinue = () => {
		const selected = getSelectedTurn()
		if (!selected) return

		if (isMultiple) {
			onConfirm(selected, { addToCart: true, continueToConfirm: false })
			return
		}

		onConfirm(selected, { continueToConfirm: true })
	}

	const handleContinueAsSingle = () => {
		const selected = getSelectedTurn()
		if (!selected) return
		onConfirm(selected, { addToCart: false, continueToConfirm: true })
	}

	const handleOpenContinueAsSingleModal = () => {
		if (!selectedTurnoId) return
		setShowContinueAsSingleModal(true)
	}

	const handleCloseContinueAsSingleModal = () => {
		setShowContinueAsSingleModal(false)
	}

	const handleConfirmContinueAsSingle = () => {
		handleCloseContinueAsSingleModal()
		handleContinueAsSingle()
	}

	const recommendedDateLabel = useMemo(() => {
		const firstCommonDate = getFirstCommonDate(allTurnos, submittedFilters)
		return firstCommonDate ? formatDayMonth(firstCommonDate) : ''
	}, [allTurnos, submittedFilters])

	return (
		<Container>
			<BackPageHeader
				onBack={onBack}
				showBack={!isMobile}
				title={<Title>Agendar turno</Title>}
				gap="6px"
			/>
			{isMultiple && <RecommendationCard dateLabel={recommendedDateLabel} />}
			<DoctorCard>
				<Avatar><UserAvatar color='#161616' /></Avatar>
				<div>
					<DoctorName>{capitalizeEachWord(nearest?.nombreProfesional)}</DoctorName>
					<DoctorMeta>{capitalizeEachWord(nearest?.nombreEspecialidad)} - {capitalizeEachWord(clinics.find(x => x.value == nearest?.clinica)?.label)}</DoctorMeta>
				</div>
			</DoctorCard>
			<Grid>
				<CalendarBlock>
					<SectionTitle>Fecha</SectionTitle>
					<DatePicker
						selected={selectedDate}
						onChange={handleSelectDate}
						includeDates={availableDates}
						disabledKeyboardNavigation
						locale="es"
						dateFormat="dd/MM/yyyy"
						inline
					/>
				</CalendarBlock>

				<HoursBlock>
					<SectionTitle>Hora</SectionTitle>
					<HoursGrid>
						{turnosByDate.map(t => (
							<HourButton
								key={t.idTurno}
								type="button"
								onClick={() => handleSelectHour(t.idTurno)}
								$active={t.idTurno === selectedTurnoId}
							>
								{formatHourMinute(t.fechaTurnoDateTime)} hs
							</HourButton>
						))}
						{turnosByDate.length === 0 && <EmptyText>No hay horarios para esta fecha</EmptyText>}
					</HoursGrid>
				</HoursBlock>
			</Grid>

			<Actions>
				<Button
					type="button"
					$variant="primary"
					disabled={!selectedTurnoId}
					onClick={handleContinue}
					label={isMultiple ? 'Agregar turno' : 'Continuar'}
					size={isMobile ? 'mobile' : 'xlarge'}
				/>
				{isMultiple && (
					<Button
						type="button"
						$variant="tertiary"
						disabled={!selectedTurnoId}
						onClick={handleOpenContinueAsSingleModal}
						label={'Continuar con turno individual'}
						size={isMobile ? 'mobile' : 'xlarge'}
					/>
				)}
			</Actions>
			<CancelAppointmentConfirmModal
				isOpen={showContinueAsSingleModal}
				onClose={handleCloseContinueAsSingleModal}
				onConfirm={handleConfirmContinueAsSingle}
				title="¿Estás seguro que querés continuar con un turno individual?"
				bodyText="Perderás tu búsqueda actual y turnos previamente seleccionados"
				confirmLabel="Sí, continuar"
				cancelLabel="Volver"
			/>
		</Container>
	)
}

function mapStateToProps({ appointment }) {
	return {
		turnosDisponibles: appointment.booking.selectedAppointments,
		allTurnos: appointment.booking.searchResults,
		submittedFilters: appointment.booking.submittedFilters,
		selectedTurn: appointment.booking.selectedTurn,
		bookingMode: appointment.booking.mode,
		clinics: appointment.booking.catalogs.clinics,
	}
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-height: 80vh;
	width: 80vw;
	padding: 8px 12px 16px 12px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		min-height: 100%;
		width: 100%;
	}
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 24px 0;
`

const DoctorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  max-width: 27%;
  min-width: 325px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		max-width: 100%;
	}
`

const Avatar = styled.div`
	width: 32px;
	height: 32px;
	display: grid;
	place-items: center;
	border: 1px solid #d0d0d0;
	border-radius: 50%;
	font-size: 16px;
`

const DoctorName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #222;
`

const DoctorMeta = styled.div`
  font-size: 14px;
  color: #4D4D4D;
`

const Grid = styled.div`
	display: flex;
	gap: 20px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		display: grid;
		grid-template-columns: 1fr;
		gap: 20px;
		align-items: flex-start;
	}
`
export const CalendarBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 27%;
	min-width: 325px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: 100%;
		min-width: 0;
	}

  /* Estilos para el DatePicker */
  .react-datepicker {
    display: block;
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    font-family: inherit;
  }

	.react-datepicker__month-container {
		float: none;
		width: 100%;
	}

	.react-datepicker-wrapper {
		width: 100%;
	}

	.react-datepicker__header {
		background-color: white;
		border-bottom: none;
		border-radius: 12px 12px 0 0;
		padding: 12px;
	}

  .react-datepicker__current-month {
    font-size: 18px;
    padding-left: 24px;
    font-weight: 600;
    color: #161616;
    width: 100%;
    text-align: left;
    text-transform: capitalize;
  }

	.react-datepicker__navigation-icon {
		width: 6px;
		height: 6px;
	}

  .react-datepicker__navigation--previous {
    left: auto;
    right: 42px;
  }

  .react-datepicker__navigation-next {
    overflow: visible;
    width: 10px;
    right: 14px;
  }

	.react-datepicker__day-names {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		gap: 2px;
		padding: 8px 12px 4px 12px;
		margin: 0;
		background: #fff;
	}

	.react-datepicker__day-name {
		font-size: 12px;
		font-weight: 600;
		color: #888;
		text-align: center;
		padding: 4px 0;
	}

	/* Ensure weekday names and day cells occupy identical columns */
	.react-datepicker__day-name,
	.react-datepicker__day {
		width: 100%;
		margin: 0;
		box-sizing: border-box;
	}

	.react-datepicker__month {
		margin: 0;
		display: grid;
		gap: 2px;
		padding: 2px 12px 12px 12px;
	}

	.react-datepicker__week {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		gap: 2px;
		margin: 0;
	}

	.react-datepicker__day {
		aspect-ratio: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 13px;
		color: #333;
		border: none;
		border-radius: 6px;
		background: #fff;
		cursor: pointer;
		transition: all 0.2s ease;
		padding: 0;

		&:hover:not(.react-datepicker__day--outside-month) {
			background: #f0f0f0;
		}
	}

	.react-datepicker__day--selected {
		background-color: #003da5;
		color: #fff;
		font-weight: 600;
		border-radius: 50%;

		&:hover {
			background-color: #003da5;
		}
	}

	.react-datepicker__day--keyboard-selected {
		background-color: transparent;
		color: #333;
		border-radius: 6px;
	}

	.react-datepicker__day--keyboard-selected.react-datepicker__day--selected {
		background-color: #003da5;
		color: #fff;
		border-radius: 50%;
	}

	.react-datepicker__day--outside-month {
		color: #ccc;
		cursor: not-allowed;
	}

	.react-datepicker__day--disabled {
		color: #ccc;
		cursor: not-allowed;
	}
`

const HoursBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 30%;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: auto;
	}
`

const SectionTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #222;
`

const HoursGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
	gap: 8px;
`

const HourButton = styled.button`
	padding: 10px 12px;
	border-radius: 8px;
	border: 1px solid ${p => (p.$active ? p.theme.colors.brand.blueOmint : '#d9d9d9')};
	background: ${p => (p.$active ? p.theme.colors.brand.blueOmint : '#fff')};
	color: ${p => (p.$active ? '#fff' : '#333')};
	font-size: 14px;
	cursor: pointer;
	transition: all 0.15s ease;

	&:hover {
		border-color: ${p => (p.$active ? p.theme.colors.brand.blueOmint : '#999')};
	}
`

const EmptyText = styled.div`
	grid-column: 1 / -1;
	font-size: 14px;
	color: #777;
`

const Actions = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	margin-top: 8px;
	width: 27%;
	min-width: 325px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: 100%;
		min-width: 0;
		max-width: 100%;
	}
`

export default connect(mapStateToProps)(SelectAppointment)
